'use client'

import { useState } from 'react'
import { ShoppingCart, Plus, Minus, X, Heart } from 'lucide-react'

interface CartItem {
  id: string
  name: string
  price: number
  image: string
  code: string
  quantity: number
  inStock: boolean
}

interface CartProps {
  isOpen: boolean
  onClose: () => void
}

export default function Cart({ isOpen, onClose }: CartProps) {
  const [cartItems, setCartItems] = useState<CartItem[]>([
    {
      id: '1',
      name: 'Premium Athletic Set',
      price: 189.99,
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=100&h=100&fit=crop',
      code: 'KX-FIT-001',
      quantity: 1,
      inStock: true
    },
    {
      id: '2',
      name: 'Luxury Handbag Replica',
      price: 289.99,
      image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=100&h=100&fit=crop',
      code: 'KX-LUX-002',
      quantity: 2,
      inStock: true
    }
  ])

  const updateQuantity = (id: string, delta: number) => {
    setCartItems(items =>
      items.map(item =>
        item.id === id
          ? { ...item, quantity: Math.max(1, item.quantity + delta) }
          : item
      )
    )
  }

  const removeItem = (id: string) => {
    setCartItems(items => items.filter(item => item.id !== id))
  }

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0)
  const shipping = subtotal > 100 ? 0 : 9.99
  const total = subtotal + shipping

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={onClose}
      />

      {/* Cart Sidebar */}
      <div className="relative right-0 w-full max-w-md bg-base-100 shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-base-300">
          <div className="flex items-center space-x-2">
            <ShoppingCart className="w-5 h-5" />
            <h2 className="text-lg font-semibold text-base-content">Shopping Cart</h2>
            <div className="badge badge-secondary">{cartItems.length}</div>
          </div>
          <button className="btn btn-ghost btn-circle" onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cartItems.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-12 h-12 text-base-content/40 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-base-content mb-2">Your cart is empty</h3>
              <p className="text-base-content/60 mb-6">Add some items to get started!</p>
              <button className="btn btn-primary" onClick={onClose}>
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div key={item.id} className="card card-compact overflow-hidden bg-base-100 shadow-sm border border-base-300">
                  <div className="card-body p-4">
                    <div className="flex space-x-4">
                      {/* Product Image */}
                      <div className="relative w-20 h-20 bg-base-200 rounded-md overflow-hidden flex-shrink-0">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Product Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start mb-2">
                          <div className="min-w-0">
                            <h4 className="font-medium text-base-content truncate">
                              {item.name}
                            </h4>
                            <div className="badge badge-primary text-xs mt-1">
                              {item.code}
                            </div>
                          </div>
                          <button
                            className="btn btn-ghost btn-circle btn-sm text-base-content/60 hover:text-error"
                            onClick={() => removeItem(item.id)}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => updateQuantity(item.id, -1)}
                              disabled={item.quantity <= 1}
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="w-8 text-center text-sm font-medium text-base-content">
                              {item.quantity}
                            </span>
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => updateQuantity(item.id, 1)}
                              disabled={!item.inStock}
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                          <span className="font-semibold text-base-content">
                            ${(item.price * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="border-t border-base-300 p-6 space-y-4">
            {/* Summary */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-base-content/60">Subtotal</span>
                <span className="font-medium text-base-content">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-base-content/60">Shipping</span>
                <span className="font-medium text-base-content">
                  {shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}
                </span>
              </div>
              {shipping > 0 && (
                <p className="text-xs text-warning-content">
                  Add ${(100 - subtotal).toFixed(2)} more for free shipping!
                </p>
              )}
              <hr className="border-base-300" />
              <div className="flex justify-between">
                <span className="font-semibold text-base-content">Total</span>
                <span className="font-bold text-lg text-base-content">${total.toFixed(2)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <button className="btn btn-primary w-full">
                Proceed to Checkout
              </button>
              <button className="btn btn-outline w-full" onClick={onClose}>
                Continue Shopping
              </button>
            </div>

            {/* Wishlist Link */}
            <div className="text-center">
              <a href="#" className="text-sm text-primary hover:text-primary-focus inline-flex items-center">
                <Heart className="w-4 h-4 mr-1" />
                Save items for later
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
